import { customFetcher } from "./http";
import { getAuthorizedRequestConfig } from "./auth";

export type TelecomTokenResponse = {
  token: string;
  expiresAt?: string;
};

/**
 * Get telecom authentication token from backend.
 * The token is associated with the current user's active wallet.
 */
export const getTelecomToken = async (): Promise<TelecomTokenResponse> =>
  customFetcher<TelecomTokenResponse>(
    "/phone-numbers/telecom/token",
    getAuthorizedRequestConfig(),
  );

/**
 * Load telecom token from storage.
 */
export const loadTelecomToken = async (): Promise<string | null> => {
  const { telecomTokenService } = await import("../../entities/user");
  return telecomTokenService.loadToken();
};

/**
 * Get current telecom token from memory (without loading from storage).
 * Note: This is synchronous but may return null if service is not initialized.
 * For guaranteed access, use ensureTelecomToken() instead.
 */
export const getCurrentTelecomToken = (): string | null => {
  // Try to get token synchronously if service is already initialized
  // This is a best-effort synchronous access
  try {
    // Use a lazy getter pattern - this will work if telecomTokenService is already imported elsewhere
    const authModule = require("../../entities/user");
    if (authModule?.telecomTokenService) {
      return authModule.telecomTokenService.getToken();
    }
  } catch {
    // Service not initialized yet, return null
  }
  return null;
};

/**
 * Fetch telecom token from backend and store it.
 */
export const fetchAndStoreTelecomToken = async (): Promise<string> => {
  const { telecomTokenService } = await import("../../entities/user");
  return telecomTokenService.fetchAndStoreToken();
};

/**
 * Ensure telecom token is available.
 * If token is not in memory/storage, fetches it from backend.
 */
export const ensureTelecomToken = async (): Promise<string | null> => {
  const { telecomTokenService } = await import("../../entities/user");
  return telecomTokenService.ensureToken();
};

/**
 * Clear telecom token from memory and storage.
 */
export const clearTelecomToken = async (): Promise<void> => {
  const { telecomTokenService } = await import("../../entities/user");
  return telecomTokenService.clearToken();
};

/**
 * Subscribe to telecom token changes.
 * Returns unsubscribe function.
 */
export const subscribeToTelecomToken = async (
  listener: (token: string | null) => void,
): Promise<() => void> => {
  const { telecomTokenService } = await import("../../entities/user");
  return telecomTokenService.subscribe(listener);
};

// Re-export types
export * from "./telecom-types";
export { TelecomApiService, TelecomApiError } from "./telecom-api.service";

